/**
 * Presentational Stripe checkout form.
 * Renders order summary, email field, Stripe Payment/Billing elements, and submit/close.
 * All content and behavior controlled by props - no business logic.
 */
import { BillingAddressElement, PaymentElement, useCheckout } from '@stripe/react-stripe-js/checkout';
import type { StripeCheckoutConfirmResult } from '@stripe/stripe-js';
import { css } from 'aphrodite';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useEventLogging } from '../../../models/Logging';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { LoadingLogo } from '../../../web/components/LoadingLogo';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { bs } from '../../../web/styles/styles';
import { asApiError } from '../../models/Api';
import { styleSheet } from './styles';
import type { StripeCheckoutFormProps, StripeCheckoutSessionSuccess } from './types';

const ERROR_MESSAGES = {
	CHECKOUT_NOT_READY: 'Payment processing failed. Please try again.',
	INVALID_EMAIL: 'Please enter a valid email address.',
	GENERIC_ERROR: 'Oops! Something went wrong. Please try again.',
} as const;

const STRIPE_ELEMENTS_LOAD_DELAY_MS = 1000;

export const StripeCheckoutForm = ({
	prefilledEmail,
	onSuccessClose,
	onCloseModal,
	sectionTitle,
	orderSummary,
	submitButtonLabel,
	loggingName = 'StripeCheckoutForm',
	isProcessingPayment,
}: StripeCheckoutFormProps) => {
	const checkout = useCheckout();
	const { logApiError } = useEventLogging(loggingName);
	let emailEditable = true;

	if (checkout?.type === 'success') {
		emailEditable = checkout.checkout.email === null;
	}

	const [isLoading, setIsLoading] = React.useState(false);
	const [isScriptLoading, setIsScriptLoading] = React.useState(true);
	const [email, setEmail] = React.useState<string>(prefilledEmail ?? '');
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const location = useLocation();

	const sessionId = checkout?.type === 'success' ? checkout.checkout.id : '';
	const checkoutSuccess = checkout?.type === 'success' ? (checkout as StripeCheckoutSessionSuccess) : null;

	React.useEffect(() => {
		return () => {
			window.onbeforeunload = null;
		};
	}, []);

	React.useEffect(() => {
		if (!location.search) {
			return;
		}

		if (checkoutSuccess) {
			onSuccessClose(checkoutSuccess.checkout.id);
		} else {
			onSuccessClose();
		}
	}, [location.search, onSuccessClose, checkoutSuccess]);

	React.useEffect(() => {
		if (checkout?.type !== 'success') return;
		const timeout = setTimeout(() => setIsScriptLoading(false), STRIPE_ELEMENTS_LOAD_DELAY_MS);
		return () => clearTimeout(timeout);
	}, [checkout?.type]);

	React.useEffect(() => {
		if (!window) {
			return;
		}

		if (isProcessingPayment) {
			window.onbeforeunload = () => {
				return 'You have attempted to leave this page. Are you sure?';
			};
			return;
		}

		window.onbeforeunload = null;
	}, [isProcessingPayment]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsLoading(true);
		setErrorMessage(null);

		if (checkout?.type !== 'success') {
			setErrorMessage(ERROR_MESSAGES.CHECKOUT_NOT_READY);
			setIsLoading(false);
			return;
		}
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setErrorMessage(ERROR_MESSAGES.INVALID_EMAIL);
			setIsLoading(false);
			return;
		}

		const confirmRequest = emailEditable ? { email } : {};

		try {
			const result: StripeCheckoutConfirmResult = await checkout.checkout.confirm(confirmRequest);
			if (result.type === 'error') {
				setErrorMessage(result.error.message);
				setIsLoading(false);
				return;
			}
			if (result.type === 'success') {
				setIsLoading(false);
				onSuccessClose(checkout.checkout.id);
			}
		} catch (err) {
			logApiError(`${loggingName}Error`, asApiError(err));
			setErrorMessage(ERROR_MESSAGES.GENERIC_ERROR);
			setIsLoading(false);
		}
	};

	const handleClose = () => onCloseModal(sessionId ?? '');

	const resolvedOrderSummary: React.ReactNode =
		typeof orderSummary === 'function'
			? checkoutSuccess
				? orderSummary(checkoutSuccess.checkout)
				: null
			: orderSummary;

	const resolvedSubmitLabel: string =
		typeof submitButtonLabel === 'function'
			? checkoutSuccess
				? submitButtonLabel(checkoutSuccess.checkout)
				: ''
			: submitButtonLabel;

	return (
		<div className={css(styleSheet.formContainer)}>
			<header className={css(styleSheet.header)}>
				<Button
					type='button'
					variant='link'
					onClick={handleClose}
					styles={[styleSheet.closeButton]}
					data-testid='checkout-close-button'
				>
					<XIcon />
				</Button>
			</header>
			<form onSubmit={handleSubmit}>
				<section className={css(bs.mb2, bs.pr1)}>
					<h3 className={css(styleSheet.paymentInfoTitle)}>{sectionTitle}</h3>
					<div className={css(styleSheet.orderInfo)}>{resolvedOrderSummary}</div>
				</section>
				<div className={css(styleSheet.emailInput)}>
					<label htmlFor='email' className={css(styleSheet.label)}>
						Email
					</label>
					<Input
						id='email'
						type='email'
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
						value={email}
						placeholder='Email'
					/>
				</div>
				{isScriptLoading ? (
					<div className={css(bs.flex, bs.flexCenter)}>
						<LoadingLogo size={50} />
					</div>
				) : null}
				<PaymentElement id='payment-element' />
				<BillingAddressElement />
				<Button
					type='submit'
					disabled={isLoading}
					variant='primary'
					size='default'
					isLoading={isLoading || isProcessingPayment}
					styles={[styleSheet.submitButton]}
				>
					{resolvedSubmitLabel}
				</Button>
				{errorMessage && <small className={css(styleSheet.errorMessage)}>{errorMessage}</small>}
			</form>
		</div>
	);
};
